import React, { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, updateDoc, addDoc, collection, query, where, getDocs, getCountFromServer, orderBy, limit, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from '../firebase/config';
import {
    X, Save, Image as ImageIcon,
    CheckCircle, AlertCircle,
    Trash2, Plus, Calendar,
    Globe, GlobeLock,
    Briefcase, Phone, Star, Receipt, Camera,
    Megaphone, MapPin
} from 'lucide-react';
import LabPortfolioPosts from './lab/LabPortfolioPosts';
import { getFeatures, isBetaUser } from '../utils/features';

// 통합 결제 서비스
import {
    loadIamportScript,
    initializeIamport,
    checkPaymentStatus
} from '../services/UnifiedPaymentService';

// --- Preset Constants ---
const SPECIALTY_PRESETS = [
    '크라운/브릿지', '임플란트 보철', '교정장치', '의치 제작/수리',
    'CAD/CAM 디지털 기공', '라미네이트/비니어', '지르코니아',
    '올세라믹', '투명교정', '스플린트', '임시 보철'
];

const SCANNER_PRESETS = ['Medit', '3Shape TRIOS', 'Primescan', 'iTero', 'Shining 3D', 'CEREC', '기타'];

const REGION_PRESETS = [
    '서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산',
    '강원', '충청', '전라', '경상', '제주', '전국 택배'
];

const PRICE_TABLE_PRESETS = [
    '지르코니아 크라운', 'PFM 크라운', 'E-max 크라운', '임플란트 크라운',
    '커스텀 어버트먼트', '인레이/온레이', '라미네이트', 'PMMA 임시치', '총의치', '교정장치'
];

const SECTION_CONFIG = [
    { num: '01', title: '기본 정보', icon: Star },
    { num: '02', title: '전문 분야', icon: Briefcase },
    { num: '03', title: '운영 정보', icon: Receipt },
    { num: '04', title: '포트폴리오', icon: ImageIcon },
    { num: '05', title: '연락처', icon: Phone },
    // { num: '06', title: '포트폴리오 게시', icon: Camera }, // 포트폴리오 피드 페이지로 이동
];

const DEFAULT_FORM = {
    slogan: '',
    representative: '',
    foundingYear: '',
    introduction: '',
    detailedIntro: '',
    globalOptIn: false,          // 🌏 해외 수주 받기 (글로벌 기획서 §2)
    introductionEn: '',          // 해외용 영문 소개
    globalLanguages: 'English',  // 대응 가능 언어
    specialties: [],
    priceTable: [],
    contactPhone: '',
    contactEmail: '',
    contactWebsite: '',
    kakaoChannelUrl: '',
    workingHours: '',
    compatibleScanners: [],
    deliveryRegions: [],
    firstDealBenefit: '',
    remakeRate: '',
    monthlyCapacity: '',
};

function LabAdvertising({ user }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [adData, setAdData] = useState(null);
    const [formData, setFormData] = useState({ ...DEFAULT_FORM });

    // Image state
    const [profileImage, setProfileImage] = useState(null);
    const [profilePreview, setProfilePreview] = useState(null);
    const [portfolioImages, setPortfolioImages] = useState([]);
    const [portfolioPreviews, setPortfolioPreviews] = useState([]);
    const [studioImages, setStudioImages] = useState([]);
    const [studioPreviews, setStudioPreviews] = useState([]);
    const [clinicShowcaseImages, setClinicShowcaseImages] = useState([]);
    const [clinicShowcasePreviews, setClinicShowcasePreviews] = useState([]);
    const [uploadingProfile, setUploadingProfile] = useState(false);
    const [uploadingPortfolio, setUploadingPortfolio] = useState(false);
    const [translatingIntro, setTranslatingIntro] = useState(false);   // 🌏 영문 소개 번역 중

    // Payment state
    const [expiryDate, setExpiryDate] = useState(null);
    const [isExpired, setIsExpired] = useState(false);
    const [daysRemaining, setDaysRemaining] = useState(0);
    const [paymentProcessing, setPaymentProcessing] = useState(false);
    const [isActive, setIsActive] = useState(false);
    // 신규 기공소 기본값: 비공개 (활성화는 기공소가 명시적으로 토글해야 함)
    const [isPublic, setIsPublic] = useState(false);

    // UI state
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    // --- 프리미엄 홍보 사전 신청 (신청만 수집, 과금·배너 노출 없음) ---
    const PROMO_TARGET = 50;                                   // 서비스 시작 기준 신청 수
    const [promoVisible, setPromoVisible] = useState(false);   // 플래그 ON 또는 베타에게만 노출
    const [promoApp, setPromoApp] = useState(null);            // 내 신청 문서 (없으면 null)
    const [promoCount, setPromoCount] = useState(null);        // 현재 신청 수 (null=미조회)
    const [promoSubmitting, setPromoSubmitting] = useState(false);
    const [promoForm, setPromoForm] = useState({ region: '', address: '', phone: '', contact: '' });
    // 신청 문서 ID = 기공소(회사) 단위. 직원이면 companyId, 아니면 본인 uid
    const promoLabId = user?.companyId || user?.uid;
    const promoApplied = promoApp?.status === 'applied';

    // --- Helpers ---
    const showSuccess = (msg) => { setSuccessMessage(msg); setErrorMessage(''); };
    const showError = (msg) => { setErrorMessage(msg); setSuccessMessage(''); };
    const updateForm = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    const toggleTag = (field, value) => {
        setFormData(prev => {
            const arr = prev[field];
            return { ...prev, [field]: arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value] };
        });
    };

    const updatePriceRow = (index, key, value) => {
        setFormData(prev => {
            const table = [...prev.priceTable];
            table[index] = { ...table[index], [key]: value };
            return { ...prev, priceTable: table };
        });
    };

    // --- Payment Logic ---
    useEffect(() => {
        const initPayment = async () => {
            try { await loadIamportScript(); initializeIamport(); }
            catch (error) { console.error('아임포트 초기화 실패:', error); }
        };
        initPayment();
    }, []);

    const calculateExpiry = useCallback((expiryDateStr) => {
        if (!expiryDateStr) return;
        const expiry = new Date(expiryDateStr);
        const now = new Date();
        const expired = expiry < now;
        setIsExpired(expired);
        setDaysRemaining(expired ? 0 : Math.ceil((expiry - now) / (1000 * 60 * 60 * 24)));
    }, []);

    const checkCurrentPaymentStatus = useCallback(async () => {
        if (!user?.uid) return;
        try {
            const status = await checkPaymentStatus(user.uid, 'lab-advertisement', user.uid);
            if (status.isPaid && status.isActive) {
                setIsActive(true);
                setExpiryDate(status.expiryDate.toISOString());
                setIsExpired(false);
                calculateExpiry(status.expiryDate.toISOString());
            } else {
                setIsActive(false);
                setIsExpired(true);
            }
        } catch (error) { console.error('결제 상태 확인 실패:', error); }
    }, [user?.uid, calculateExpiry]);

    // --- Load Data ---
    const loadAdvertisement = useCallback(async () => {
        if (!user) return;
        try {
            setLoading(true);
            const adRef = doc(db, 'labAdvertisements', user.uid);
            const adSnap = await getDoc(adRef);

            if (adSnap.exists()) {
                const data = adSnap.data();
                setAdData(data);
                setFormData({
                    slogan: data.slogan || '',
                    representative: data.representative || '',
                    foundingYear: data.foundingYear || '',
                    introduction: data.introduction || '',
                    detailedIntro: data.detailedIntro || data.philosophy || '',
                    globalOptIn: !!data.globalOptIn,
                    introductionEn: data.introductionEn || '',
                    globalLanguages: data.globalLanguages || 'English',
                    specialties: Array.isArray(data.specialties) ? data.specialties :
                        (Array.isArray(data.services) ? data.services.map(s => typeof s === 'string' ? s : s.name) : []),
                    priceTable: Array.isArray(data.priceTable) ? data.priceTable : [],
                    contactPhone: data.contactPhone || '',
                    contactEmail: data.contactEmail || data.contactInfo || '',
                    contactWebsite: data.contactWebsite || '',
                    kakaoChannelUrl: data.kakaoChannelUrl || '',
                    workingHours: data.workingHours || '',
                    compatibleScanners: Array.isArray(data.compatibleScanners) ? data.compatibleScanners : [],
                    deliveryRegions: Array.isArray(data.deliveryRegions) ? data.deliveryRegions : [],
                    firstDealBenefit: data.firstDealBenefit || '',
                    remakeRate: data.remakeRate || '',
                    monthlyCapacity: data.monthlyCapacity || '',
                });
                setProfilePreview(data.profileImageUrl || null);
                setPortfolioPreviews(data.portfolioImages || []);
                setStudioPreviews(data.studioImages || []);
                setClinicShowcasePreviews(data.clinicShowcaseImages || []);
            }

            const userRef = doc(db, 'users', user.uid);
            const userSnap = await getDoc(userRef);
            // 기본값: 비공개. 명시적으로 isPublic === true 인 경우에만 공개 상태로 표시
            if (userSnap.exists()) setIsPublic(userSnap.data().isPublic === true);

            await checkCurrentPaymentStatus();
        } catch (error) {
            console.error('광고 데이터 로드 실패:', error);
            showError('데이터를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [user, checkCurrentPaymentStatus]);

    useEffect(() => { loadAdvertisement(); }, [loadAdvertisement]);

    // --- 프리미엄 홍보 사전 신청: 노출 여부·내 신청·현재 신청 수 로드 ---
    const loadPromo = useCallback(async () => {
        if (!user || !promoLabId) return;
        try {
            const features = await getFeatures();
            const visible = features?.labPromoWaitlist === true || isBetaUser(features, user, user.email);
            setPromoVisible(visible);
            if (!visible) return;

            // 내 신청 문서 (연락처·주소 포함, 본인/관리자만 읽힘)
            const mySnap = await getDoc(doc(db, 'labPromoApplications', promoLabId));
            const mine = mySnap.exists() ? mySnap.data() : null;
            setPromoApp(mine);
            // 폼 프리필: 기존 신청값 우선, 없으면 user 문서 값
            setPromoForm({
                region: mine?.region || user.region || (user.address ? user.address.split(' ')[0] : '') || '',
                address: mine?.address || user.address || '',
                phone: mine?.phone || user.phone || '',
                contact: mine?.contact || user.representativeName || user.businessName || '',
            });

            // 현재 신청 수: 개인정보 없는 공개 신호 컬렉션에서 카운트만 집계 (문서 내용 미다운로드)
            try {
                const cntSnap = await getCountFromServer(
                    query(collection(db, 'labPromoWaitlistPublic'), where('status', '==', 'applied'))
                );
                setPromoCount(cntSnap.data().count);
            } catch (_) { /* 카운트 실패는 표시만 생략, 신청 기능은 유지 */ }
        } catch (e) {
            console.error('사전 신청 정보 로드 실패:', e);
        }
    }, [user, promoLabId]);

    useEffect(() => { loadPromo(); }, [loadPromo]);

    // 사전 신청 제출 (PII는 labPromoApplications, 공개 카운트용 무-PII 신호는 labPromoWaitlistPublic)
    const submitPromo = async () => {
        if (!promoLabId) return;
        if (!promoForm.phone.trim()) { showError('연락처를 입력해주세요.'); return; }
        try {
            setPromoSubmitting(true);
            const now = new Date();
            const alreadyExists = !!promoApp;
            const payload = {
                labId: promoLabId,
                businessName: user.businessName || '',
                region: promoForm.region.trim(),
                address: promoForm.address.trim(),
                phone: promoForm.phone.trim(),
                contact: promoForm.contact.trim(),
                status: 'applied',
                updatedAt: now,
                ...(alreadyExists ? {} : { appliedAt: now }),
            };
            await setDoc(doc(db, 'labPromoApplications', promoLabId), payload, { merge: true });
            // 공개 카운트용 신호(개인정보 없음): 상태·시각만
            await setDoc(doc(db, 'labPromoWaitlistPublic', promoLabId), { status: 'applied', updatedAt: now }, { merge: true });
            showSuccess('사전 신청이 접수되었습니다. 지금은 과금되지 않으며, 50곳이 모여 서비스 시작 시 안내드립니다.');
            await loadPromo();
        } catch (e) {
            console.error('사전 신청 실패:', e);
            showError('신청에 실패했습니다. 잠시 후 다시 시도해주세요.');
        } finally { setPromoSubmitting(false); }
    };

    // 신청 취소 (문서는 남기고 status만 cancelled — 이력 보존)
    const cancelPromo = async () => {
        if (!promoLabId || !promoApp) return;
        try {
            setPromoSubmitting(true);
            const now = new Date();
            await updateDoc(doc(db, 'labPromoApplications', promoLabId), { status: 'cancelled', updatedAt: now });
            await setDoc(doc(db, 'labPromoWaitlistPublic', promoLabId), { status: 'cancelled', updatedAt: now }, { merge: true });
            showSuccess('사전 신청이 취소되었습니다.');
            await loadPromo();
        } catch (e) {
            console.error('신청 취소 실패:', e);
            showError('취소에 실패했습니다.');
        } finally { setPromoSubmitting(false); }
    };
    useEffect(() => { if (successMessage) { const t = setTimeout(() => setSuccessMessage(''), 3000); return () => clearTimeout(t); } }, [successMessage]);
    useEffect(() => { if (errorMessage) { const t = setTimeout(() => setErrorMessage(''), 3000); return () => clearTimeout(t); } }, [errorMessage]);

    useEffect(() => { if (expiryDate) calculateExpiry(expiryDate); }, [expiryDate, calculateExpiry]);

    // --- 아이반 예시 데이터 ---
    const fillSampleData = () => {
        setFormData({
            slogan: '정밀한 디지털 기공, 신뢰할 수 있는 파트너',
            representative: '김승우',
            foundingYear: '2024',
            introduction: '구강스캐너 전문 디지털 기공소, 모든 주요 스캐너 호환',
            detailedIntro: '구강스캐너 전문 디지털 기공소로서, 치과 의료진에게 정밀하고 재현성 높은 보철물을 제공합니다. 디지털 워크플로우를 통해 기존 아날로그 방식의 한계를 넘어, 더 빠르고 정확한 결과물을 약속합니다.',
            specialties: ['크라운/브릿지', '임플란트 보철', 'CAD/CAM 디지털 기공', '지르코니아', '라미네이트/비니어', '임시 보철'],
            priceTable: [
                { item: '지르코니아 크라운', price: '55000', deliveryDays: '3' },
                { item: '임플란트 크라운', price: '80000', deliveryDays: '5' },
                { item: '커스텀 어버트먼트', price: '55000', deliveryDays: '5' },
                { item: 'PFM 크라운', price: '45000', deliveryDays: '3' },
                { item: '인레이/온레이', price: '45000', deliveryDays: '3' },
                { item: '라미네이트', price: '50000', deliveryDays: '4' },
                { item: 'PMMA 임시치', price: '22000', deliveryDays: '1' },
            ],
            contactPhone: '010-2972-3733',
            contactEmail: 'ivanlab@naver.com',
            contactWebsite: 'www.ivandentallab.co.kr',
            workingHours: '평일 09:00~18:00 / 토 09:00~13:00',
            compatibleScanners: ['Medit', '3Shape TRIOS', 'Primescan', 'iTero', 'Shining 3D'],
            deliveryRegions: ['서울', '경기', '인천', '전국 택배'],
            firstDealBenefit: '',   // 할인 문구 샘플 제거 — 의료법 가이드 (2026-07-19)
            remakeRate: '2',
            monthlyCapacity: '150',
        });
        showSuccess('아이반치과기공소 예시 데이터가 입력되었습니다. "저장" 버튼을 눌러 저장하세요.');
    };

    // --- Image Handlers ---
    const handleImageUpload = (e, setFile, setPreview, maxMB = 5) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > maxMB * 1024 * 1024) { showError(`이미지는 ${maxMB}MB 이하로 업로드해주세요.`); return; }
        setFile(file);
        setPreview(URL.createObjectURL(file));
    };

    const handlePortfolioImagesChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + portfolioPreviews.length > 20) {
            showError('포트폴리오 이미지는 최대 20개까지 업로드 가능합니다.');
            return;
        }
        const validFiles = files.filter(file => {
            if (file.size > 5 * 1024 * 1024) { showError(`${file.name}은(는) 5MB를 초과합니다.`); return false; }
            return true;
        });
        setPortfolioImages(prev => [...prev, ...validFiles]);
        setPortfolioPreviews(prev => [...prev, ...validFiles.map(f => URL.createObjectURL(f))]);
    };

    const removePortfolioImage = (index) => {
        setPortfolioImages(prev => prev.filter((_, i) => i !== index));
        setPortfolioPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleStudioImagesChange = (e) => {
        const files = Array.from(e.target.files);
        if (files.length + studioPreviews.length > 10) {
            showError('내부사진은 최대 10개까지 업로드 가능합니다.');
            return;
        }
        const validFiles = files.filter(file => {
            if (file.size > 5 * 1024 * 1024) { showError(`${file.name}은(는) 5MB를 초과합니다.`); return false; }
            return true;
        });
        setStudioImages(prev => [...prev, ...validFiles]);
        setStudioPreviews(prev => [...prev, ...validFiles.map(f => URL.createObjectURL(f))]);
    };

    const removeStudioImage = (index) => {
        setStudioImages(prev => prev.filter((_, i) => i !== index));
        setStudioPreviews(prev => prev.filter((_, i) => i !== index));
    };

    const userIsSubscribed = user?.isSubscribed === true;

    // --- Toggle Public ---
    const handleTogglePublic = async () => {
        // 구독·승인 없이 즉시 활성화/비활성화
        try {
            setSaving(true);
            const newPublicState = !isPublic;
            await updateDoc(doc(db, 'users', user.uid), { isPublic: newPublicState, updatedAt: new Date().toISOString() });
            try { await updateDoc(doc(db, 'labAdvertisements', user.uid), { isActive: newPublicState, updatedAt: new Date().toISOString() }); } catch (e) { /* no ad doc yet */ }
            setIsPublic(newPublicState);
            setIsActive(newPublicState);
            showSuccess(newPublicState ? '홍보페이지가 활성화되었습니다! 기공소찾기에 바로 노출됩니다.' : '기공소가 비공개로 전환되었습니다.');
        } catch (error) { showError('설정 변경에 실패했습니다.'); }
        finally { setSaving(false); }
    };

    // --- Region helper ---
    const extractRegion = (address) => {
        if (!address) return '';
        const regions = ['서울', '경기', '인천', '부산', '대구', '광주', '대전', '울산', '세종', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'];
        for (const region of regions) { if (address.includes(region)) return region; }
        return address.split(' ')[0] || '';
    };

    // --- Save ---
    const handleSave = async () => {
        if (formData.specialties.length === 0) { showError('최소 1개 이상의 전문 분야를 선택해주세요.'); return; }
        try {
            setSaving(true);
            const userDoc = await getDoc(doc(db, 'users', user.uid));
            const userData = userDoc.data();

            let profileImageUrl = adData?.profileImageUrl || null;
            // 현재 화면에 보이는 URL만 유지 (삭제 반영)
            // portfolioPreviews에는 기존 URL + 새 blob URL이 섞여있음
            // 기존 URL만 필터 (blob:이 아닌 것)
            let portfolioUrls = portfolioPreviews.filter(u => !u.startsWith('blob:'));
            let studioUrls = studioPreviews.filter(u => !u.startsWith('blob:'));

            if (profileImage) {
                setUploadingProfile(true);
                const profileRef = ref(storage, `labAds/${user.uid}/profile_${Date.now()}.jpg`);
                await uploadBytes(profileRef, profileImage);
                profileImageUrl = await getDownloadURL(profileRef);
                setUploadingProfile(false);
            }

            if (portfolioImages.length > 0) {
                setUploadingPortfolio(true);
                const uploadPromises = portfolioImages.map(async (file, index) => {
                    const portfolioRef = ref(storage, `labAds/${user.uid}/portfolio_${Date.now()}_${index}.jpg`);
                    await uploadBytes(portfolioRef, file);
                    return await getDownloadURL(portfolioRef);
                });
                const newUrls = await Promise.all(uploadPromises);
                portfolioUrls = [...portfolioUrls, ...newUrls];
                setUploadingPortfolio(false);
            }

            if (studioImages.length > 0) {
                const uploadPromises = studioImages.map(async (file, index) => {
                    const studioRef = ref(storage, `labAds/${user.uid}/studio_${Date.now()}_${index}.jpg`);
                    await uploadBytes(studioRef, file);
                    return await getDownloadURL(studioRef);
                });
                const newUrls = await Promise.all(uploadPromises);
                studioUrls = [...studioUrls, ...newUrls];
            }

            // Backward compat
            const specialties = formData.specialties;
            const introduction = formData.introduction || '';
            const philosophy = formData.detailedIntro || '';

            // 주소 → 좌표 변환 (카카오 지오코더)
            let lat = adData?.lat || null;
            let lng = adData?.lng || null;
            const addressForGeo = userData?.address || formData.address || '';
            if (addressForGeo && window.kakao?.maps) {
                try {
                    const coords = await new Promise((resolve) => {
                        const geocoder = new window.kakao.maps.services.Geocoder();
                        geocoder.addressSearch(addressForGeo, (result, status) => {
                            if (status === window.kakao.maps.services.Status.OK && result.length > 0) {
                                resolve({ lat: parseFloat(result[0].y), lng: parseFloat(result[0].x) });
                            } else {
                                resolve(null);
                            }
                        });
                    });
                    if (coords) { lat = coords.lat; lng = coords.lng; }
                } catch (e) { console.log('Geocoding failed:', e); }
            }

            const adRef = doc(db, 'labAdvertisements', user.uid);
            const adDataToSave = {
                ...formData,
                lat,
                lng,
                profileImageUrl,
                heroImageUrl: profileImageUrl || '',
                portfolioImages: portfolioUrls,
                studioImages: studioUrls,
                clinicShowcaseImages: clinicShowcasePreviews,
                // Backward compat fields
                specialties,
                introduction,
                philosophy,
                services: specialties.map(s => ({ name: s, description: '', keywords: '' })),
                // Meta
                isActive: isPublic,
                status: isPublic ? 'active' : 'inactive',
                labId: user.uid,
                businessName: userData?.businessName || '',
                email: userData?.email || '',
                phone: userData?.phone || '',
                address: userData?.address || '',
                sido: extractRegion(userData?.address) || '', // 위치 기반 분할용 (옵션 B)
                updatedAt: new Date(),
                createdAt: adData?.createdAt || new Date()
            };

            if (adData) { await updateDoc(adRef, adDataToSave); }
            else { await setDoc(adRef, adDataToSave); }

            setProfileImage(null);
            setPortfolioImages([]);
            setStudioImages([]);

            // Sync matchingLabs
            const matchingRef = doc(db, 'matchingLabs', user.uid);
            const matchingDoc2 = await getDoc(matchingRef);
            if (matchingDoc2.exists()) {
                await updateDoc(matchingRef, {
                    labName: userData?.businessName || userData?.companyName || '',
                    email: userData?.email || '',
                    phone: userData?.phone || '',
                    region: extractRegion(userData?.address) || userData?.address || '',
                    detailAddress: userData?.address || '',
                    specialties,
                    introduction,
                    updatedAt: serverTimestamp()
                });
            }

            await loadAdvertisement();
            showSuccess('광고가 저장되었습니다.');
        } catch (error) {
            console.error('저장 실패:', error);
            showError('저장에 실패했습니다.');
        } finally {
            setSaving(false);
            setUploadingProfile(false);
            setUploadingPortfolio(false);
        }
    };

    // --- Free Activation + Matching Pool ---
    const handlePayment = async () => {
        if (!adData) { showError('먼저 광고 내용을 저장해주세요.'); return; }
        try {
            setPaymentProcessing(true);
            const expiryDateValue = new Date();
            expiryDateValue.setDate(expiryDateValue.getDate() + 30);

            const userDoc = await getDoc(doc(db, 'users', user.uid));
            const userData = userDoc.exists() ? userDoc.data() : {};

            const adRef = doc(db, 'labAdvertisements', user.uid);
            await updateDoc(adRef, {
                status: 'active', isActive: true,
                expiryDate: expiryDateValue.toISOString(),
                activatedAt: new Date(), updatedAt: new Date()
            });

            const matchingRef = doc(db, 'matchingLabs', user.uid);
            await setDoc(matchingRef, {
                labId: user.uid,
                labName: userData.businessName || userData.companyName || userData.name || '',
                email: userData.email || user.email || '',
                phone: userData.phone || '',
                region: extractRegion(userData.address) || userData.address || '',
                detailAddress: userData.address || '',
                specialties: formData.specialties,
                introduction: formData.introduction || '',
                monthlyCapacity: 'over50',
                avgDeliveryDays: '3~5일',
                plan: 'basic',
                isActive: true,
                subscribedAt: serverTimestamp(),
                expiresAt: expiryDateValue,
                updatedAt: serverTimestamp()
            }, { merge: true });

            showSuccess('광고가 활성화되었습니다! 치과 매칭 서비스에도 자동 등록되었습니다.');
            setExpiryDate(expiryDateValue.toISOString());
            setIsActive(true);
            setIsExpired(false);
            calculateExpiry(expiryDateValue.toISOString());
            await loadAdvertisement();
        } catch (error) {
            console.error('활성화 실패:', error);
            showError(error.message || '활성화에 실패했습니다.');
        } finally { setPaymentProcessing(false); }
    };

    // --- Add price row ---
    const addPriceRow = (itemName) => {
        if (formData.priceTable.some(r => r.item === itemName)) return;
        setFormData(prev => ({ ...prev, priceTable: [...prev.priceTable, { item: itemName, price: '', deliveryDays: '' }] }));
    };

    const removePriceRow = (index) => {
        setFormData(prev => ({ ...prev, priceTable: prev.priceTable.filter((_, i) => i !== index) }));
    };

    // =========================================================================
    // RENDER
    // =========================================================================

    if (loading) {
        return <div style={S.loading}><div style={S.spinner} /><p>로딩 중...</p></div>;
    }

    const SectionHeader = ({ cfg }) => {
        const Icon = cfg.icon;
        return (
            <div style={S.sectionHeader}>
                <div style={S.sectionNum}>{cfg.num}</div>
                <div style={S.sectionIcon}><Icon size={20} color="#6366f1" /></div>
                <h2 style={S.sectionTitle}>{cfg.title}</h2>
            </div>
        );
    };

    const PillToggle = ({ items, selected, onToggle }) => (
        <div style={S.pillWrap}>
            {items.map(item => (
                <button key={item} type="button" onClick={() => onToggle(item)}
                    style={{ ...S.pill, ...(selected.includes(item) ? S.pillActive : {}) }}>
                    {item}
                </button>
            ))}
        </div>
    );

    return (
        <div style={S.container}>
            {/* Page Header */}
            <div style={S.pageHeader}>
                <div>
                    <h1 style={S.pageTitle}>기공소 홍보 관리</h1>
                    <p style={S.pageSubtitle}>치과에 우리 기공소를 전문적으로 홍보하세요</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {user?.email === 'ivanlab@naver.com' && (
                        <button onClick={fillSampleData} style={S.sampleBtn}>예시 데이터 채우기</button>
                    )}
                    {expiryDate && !isExpired && (
                        <div style={S.expiryBadge}><Calendar size={16} /><span>{daysRemaining}일 남음</span></div>
                    )}
                </div>
            </div>

            {/* Messages */}
            {successMessage && <div style={S.successMsg}><CheckCircle size={20} />{successMessage}</div>}
            {errorMessage && <div style={S.errorMsg}><AlertCircle size={20} />{errorMessage}</div>}

            {/* ============ 프리미엄 홍보 사전 신청 (신청만 수집, 과금·배너 노출 없음) ============ */}
            {promoVisible && (
                <div style={S.promoCard}>
                    <div style={S.promoHeader}>
                        <div style={S.promoIconBox}><Megaphone size={22} color="#fff" /></div>
                        <div>
                            <div style={S.promoTitle}>프리미엄 홍보 사전 신청</div>
                            <div style={S.promoBadge}>사전 신청 · 지금은 과금 없음</div>
                        </div>
                    </div>

                    <p style={S.promoLead}>
                        {'치과 커뮤니티 사이트 상단 배너에 우리 기공소를 홍보합니다. 원장님에게는 지역별로 가까운 기공소 순서로 노출됩니다.'}
                    </p>

                    <div style={S.promoInfoGrid}>
                        <div style={S.promoInfoItem}>
                            <div style={S.promoInfoLabel}>노출 위치</div>
                            <div style={S.promoInfoValue}>{'치과 커뮤니티 사이트 상단 배너'}</div>
                        </div>
                        <div style={S.promoInfoItem}>
                            <div style={S.promoInfoLabel}>노출 방식</div>
                            <div style={S.promoInfoValue}>{'지역별 가까운 기공소 순'}</div>
                        </div>
                        <div style={S.promoInfoItem}>
                            <div style={S.promoInfoLabel}>이용 금액</div>
                            <div style={S.promoInfoValue}>{'월 10만원'}</div>
                        </div>
                        <div style={S.promoInfoItem}>
                            <div style={S.promoInfoLabel}>시작 조건</div>
                            <div style={S.promoInfoValue}>{'신청 기공소 50곳 이상'}</div>
                        </div>
                    </div>

                    <div style={S.promoNotice}>
                        <AlertCircle size={16} color="#b45309" style={{ flexShrink: 0, marginTop: 1 }} />
                        <span>{'이 신청은 관심 등록(대기 명단)이며, 지금은 과금되지 않습니다. 신청 기공소가 50곳에 도달해 서비스가 시작될 때 별도 안내와 동의(신청)를 받은 뒤에만 과금됩니다. 50곳에 못 미치면 신청은 자동으로 소멸되며, 어떤 비용이나 의무도 없습니다.'}</span>
                    </div>

                    {promoCount !== null && (
                        <div style={S.promoProgressWrap}>
                            <div style={S.promoProgressText}>
                                {`현재 ${promoCount}곳 신청`}
                                {promoCount < PROMO_TARGET
                                    ? ` · 50곳까지 ${PROMO_TARGET - promoCount}곳 남음`
                                    : ` · 목표 달성`}
                            </div>
                            <div style={S.promoProgressBar}>
                                <div style={{ ...S.promoProgressFill, width: `${Math.min(100, Math.round((promoCount / PROMO_TARGET) * 100))}%` }} />
                            </div>
                        </div>
                    )}

                    {promoApplied ? (
                        <div style={S.promoAppliedBox}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <CheckCircle size={18} color="#16a34a" />
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#166534' }}>{'신청 완료'}</span>
                            </div>
                            <div style={{ fontSize: 13, color: '#475569', marginTop: 6 }}>
                                {'서비스 시작 시 아래 연락처로 안내드립니다.'}
                                {promoForm.phone ? ` (${promoForm.phone})` : ''}
                            </div>
                            <button onClick={cancelPromo} disabled={promoSubmitting} style={S.promoCancelBtn}>
                                {promoSubmitting ? '처리 중...' : '신청 취소'}
                            </button>
                        </div>
                    ) : (
                        <div style={S.promoFormBox}>
                            <div style={S.promoFormRow}>
                                <div style={{ ...S.formGroup, flex: 1 }}>
                                    <label style={S.label}><MapPin size={13} style={{ verticalAlign: 'middle', marginRight: 4 }} />{'지역'}</label>
                                    <input type="text" value={promoForm.region}
                                        onChange={e => setPromoForm(p => ({ ...p, region: e.target.value }))}
                                        placeholder="예: 서울" style={S.input} />
                                </div>
                                <div style={{ ...S.formGroup, flex: 2 }}>
                                    <label style={S.label}>{'주소'}</label>
                                    <input type="text" value={promoForm.address}
                                        onChange={e => setPromoForm(p => ({ ...p, address: e.target.value }))}
                                        placeholder="기공소 주소" style={S.input} />
                                </div>
                            </div>
                            <div style={S.promoFormRow}>
                                <div style={{ ...S.formGroup, flex: 1 }}>
                                    <label style={S.label}>{'연락처'}</label>
                                    <input type="text" value={promoForm.phone}
                                        onChange={e => setPromoForm(p => ({ ...p, phone: e.target.value }))}
                                        placeholder="010-0000-0000" style={S.input} />
                                </div>
                                <div style={{ ...S.formGroup, flex: 1 }}>
                                    <label style={S.label}>{'담당자'}</label>
                                    <input type="text" value={promoForm.contact}
                                        onChange={e => setPromoForm(p => ({ ...p, contact: e.target.value }))}
                                        placeholder="담당자 이름" style={S.input} />
                                </div>
                            </div>
                            <button onClick={submitPromo} disabled={promoSubmitting} style={S.promoApplyBtn}>
                                <Megaphone size={16} />
                                {promoSubmitting ? '신청 중...' : '사전 신청하기'}
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Status Card: Public Toggle */}
            <div style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ ...S.iconBox, backgroundColor: isPublic ? '#6366f1' : '#94a3b8' }}>
                            {isPublic ? <Globe size={20} color="#fff" /> : <GlobeLock size={20} color="#fff" />}
                        </div>
                        <div>
                            <div style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{isPublic ? '공개 중' : '비공개'}</div>
                            <div style={{ fontSize: '13px', color: '#64748b' }}>{isPublic ? '기공소 찾기에 노출됩니다' : '기공소 찾기에 노출되지 않습니다'}</div>
                        </div>
                        <button onClick={handleTogglePublic} disabled={saving}
                            style={{ ...S.toggle, backgroundColor: isPublic ? '#10b981' : '#94a3b8', opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                            <div style={{ ...S.toggleKnob, transform: isPublic ? 'translateX(24px)' : 'translateX(0)' }} />
                        </button>
                    </div>
                    {expiryDate && !isExpired && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={S.statusActive}><CheckCircle size={14} />활성</div>
                            <div style={{ fontSize: '13px', color: '#64748b' }}>만료: {new Date(expiryDate).toLocaleDateString('ko-KR')}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* 🌏 글로벌 수주 (해외 기공소 대상 노출) */}
            <div style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>🌏 해외 수주 받기 (Global)</div>
                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                            켜면 해외 기공소의 기공소찾기(영문)에 노출됩니다. 발주 협의는 번역 채팅, 파일은 에이전트로 오갑니다. 수수료 5% (확정 인보이스 기준).
                        </div>
                    </div>
                    <button onClick={() => setFormData(prev => ({ ...prev, globalOptIn: !prev.globalOptIn }))}
                        style={{ ...S.toggle, backgroundColor: formData.globalOptIn ? '#10b981' : '#94a3b8' }}>
                        <div style={{ ...S.toggleKnob, transform: formData.globalOptIn ? 'translateX(24px)' : 'translateX(0)' }} />
                    </button>
                </div>
                {formData.globalOptIn && (
                    <div style={{ marginTop: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>영문 소개 (English introduction)</div>
                            <button
                                onClick={async () => {
                                    const src = [formData.introduction, formData.detailedIntro].filter(Boolean).join('\n\n').trim();
                                    if (!src) { showError('먼저 아래 기본 정보의 한글 소개(한 줄 소개·상세 소개)를 작성해주세요.'); return; }
                                    setTranslatingIntro(true);
                                    try {
                                        const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'translateText');
                                        const res = await fn({ text: src, targetLang: 'en' });
                                        const translated = res?.data?.translated || '';
                                        if (!translated) throw new Error('empty');
                                        setFormData(prev => ({ ...prev, introductionEn: translated }));
                                        showSuccess('한글 소개를 영문으로 번역했습니다. 내용을 확인·수정 후 저장하세요.');
                                    } catch (e) {
                                        console.error('소개 번역 실패:', e);
                                        showError('번역에 실패했습니다. 잠시 후 다시 시도해주세요.');
                                    } finally {
                                        setTranslatingIntro(false);
                                    }
                                }}
                                disabled={translatingIntro}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', borderRadius: 7, border: '1px solid #6366f1', background: '#fff', color: '#6366f1', fontSize: 12.5, fontWeight: 700, cursor: translatingIntro ? 'wait' : 'pointer' }}>
                                🌐 {translatingIntro ? '번역 중…' : '한글 소개 번역하기'}
                            </button>
                        </div>
                        <textarea
                            value={formData.introductionEn}
                            onChange={(e) => setFormData(prev => ({ ...prev, introductionEn: e.target.value }))}
                            placeholder="e.g. Digital dental lab specializing in zirconia crowns and implant prosthetics. exocad/3Shape workflow, 2-day turnaround."
                            style={{ width: '100%', minHeight: 80, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', resize: 'vertical' }}
                        />
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#334155', margin: '10px 0 6px' }}>대응 가능 언어</div>
                        <input
                            value={formData.globalLanguages}
                            onChange={(e) => setFormData(prev => ({ ...prev, globalLanguages: e.target.value }))}
                            placeholder="English"
                            style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }}
                        />
                        <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>저장 버튼을 눌러야 반영됩니다. 프로필 공개(위 토글)가 켜져 있어야 노출됩니다.</div>
                    </div>
                )}
            </div>

            {/* ============ SECTION 1: 기본 정보 ============ */}
            <div style={S.card}>
                <SectionHeader cfg={SECTION_CONFIG[0]} />
                <div style={S.sectionBody}>
                    {/* Profile Image */}
                    <div style={S.formGroup}>
                        <label style={S.label}>프로필 이미지</label>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <div style={{ width: '160px', height: '160px', position: 'relative' }}>
                                {profilePreview ? (
                                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                                        <img src={profilePreview} alt="프로필" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', border: '3px solid #e2e8f0' }} />
                                        <button onClick={() => { setProfileImage(null); setProfilePreview(null); }} style={{ ...S.removeBtn, top: '4px', right: '4px' }} type="button"><X size={14} /></button>
                                    </div>
                                ) : (
                                    <label style={{ ...S.uploadBox, width: '100%', height: '100%', borderRadius: '50%' }}>
                                        <ImageIcon size={24} color="#94a3b8" />
                                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>사진 등록</span>
                                        <input type="file" accept="image/*" onChange={e => handleImageUpload(e, setProfileImage, setProfilePreview)} style={S.fileInput} />
                                    </label>
                                )}
                            </div>
                        </div>
                    </div>
                    <div style={S.formGroup}>
                        <label style={S.label}>슬로건</label>
                        <input type="text" value={formData.slogan} onChange={e => updateForm('slogan', e.target.value)}
                            placeholder='예: "정밀한 기술, 안정된 품질"' style={S.input} />
                    </div>
                    <div style={S.formRow}>
                        <div style={{ ...S.formGroup, flex: 1 }}>
                            <label style={S.label}>대표자명</label>
                            <input type="text" value={formData.representative} onChange={e => updateForm('representative', e.target.value)}
                                placeholder="대표자 이름" style={S.input} />
                        </div>
                        <div style={{ ...S.formGroup, flex: 1 }}>
                            <label style={S.label}>설립연도</label>
                            <input type="text" value={formData.foundingYear} onChange={e => updateForm('foundingYear', e.target.value)}
                                placeholder="예: 2024" style={S.input} />
                        </div>
                    </div>
                    <div style={S.formGroup}>
                        <label style={S.label}>한줄 소개</label>
                        <input type="text" value={formData.introduction} onChange={e => updateForm('introduction', e.target.value)}
                            placeholder="기공소를 한 문장으로 소개해주세요" style={S.input} />
                    </div>
                    <div style={S.formGroup}>
                        <label style={S.label}>상세 소개</label>
                        <textarea value={formData.detailedIntro} onChange={e => updateForm('detailedIntro', e.target.value)}
                            placeholder="기공소의 강점, 비전, 철학 등을 자유롭게 작성해주세요" rows={5} style={S.textarea} />
                    </div>
                </div>
            </div>

            {/* ============ SECTION 2: 전문 분야 ============ */}
            <div style={S.card}>
                <SectionHeader cfg={SECTION_CONFIG[1]} />
                <div style={S.sectionBody}>
                    <div style={S.formGroup}>
                        <label style={S.label}>전문 분야</label>
                        <p style={S.hint}>해당되는 항목을 모두 선택하세요</p>
                        <PillToggle items={SPECIALTY_PRESETS} selected={formData.specialties} onToggle={v => toggleTag('specialties', v)} />
                    </div>
                    <div style={S.formGroup}>
                        <label style={S.label}>호환 스캐너</label>
                        <PillToggle items={SCANNER_PRESETS} selected={formData.compatibleScanners} onToggle={v => toggleTag('compatibleScanners', v)} />
                    </div>
                    <div style={S.formGroup}>
                        <label style={S.label}>배송 가능 지역</label>
                        <PillToggle items={REGION_PRESETS} selected={formData.deliveryRegions} onToggle={v => toggleTag('deliveryRegions', v)} />
                    </div>
                </div>
            </div>

            {/* ============ SECTION 3: 운영 정보 ============ */}
            <div style={S.card}>
                <SectionHeader cfg={SECTION_CONFIG[2]} />
                <div style={S.sectionBody}>
                    {/* 단가표 입력 — 가격 비공개 정책으로 제거 (2026-07) */}
                    <div style={S.formRow}>
                        <div style={{ ...S.formGroup, flex: 1 }}>
                            <label style={S.label}>리메이크율</label>
                            <div style={S.inputWithSuffix}>
                                <input type="text" value={formData.remakeRate} onChange={e => updateForm('remakeRate', e.target.value)}
                                    placeholder="예: 2" style={S.inputInner} />
                                <span style={S.suffix}>%</span>
                            </div>
                        </div>
                        <div style={{ ...S.formGroup, flex: 1 }}>
                            <label style={S.label}>월 처리 역량</label>
                            <div style={S.inputWithSuffix}>
                                <input type="text" value={formData.monthlyCapacity} onChange={e => updateForm('monthlyCapacity', e.target.value)}
                                    placeholder="예: 150" style={S.inputInner} />
                                <span style={S.suffix}>건/월</span>
                            </div>
                        </div>
                    </div>
                    {/* 첫 거래 혜택(할인) 입력 — 의료법 가이드(할인 표시 금지)에 따라 제거 (2026-07-19)
                    <div style={S.formGroup}>
                        <label style={S.label}>첫 거래 혜택</label>
                        <input type="text" value={formData.firstDealBenefit} onChange={e => updateForm('firstDealBenefit', e.target.value)}
                            placeholder='예: "첫 3케이스 30% 할인"' style={S.input} />
                    </div> */}
                </div>
            </div>

            {/* ============ SECTION 4: 포트폴리오 ============ */}
            <div style={S.card}>
                <SectionHeader cfg={SECTION_CONFIG[3]} />
                <div style={S.sectionBody}>
                    {/* 작업물 사진 */}
                    <div style={S.formGroup}>
                        <label style={S.label}>작업물 사진 (최대 20장, 각 5MB)</label>
                        <p style={S.hint}>완성된 보철물, 작업 과정 등 작업물 사진을 올려주세요</p>
                        <div style={S.portfolioGrid}>
                            {portfolioPreviews.map((url, i) => (
                                <div key={i} style={S.portfolioItem}>
                                    <img src={url} alt={`작업물 ${i + 1}`} style={S.portfolioImg} />
                                    <button onClick={() => removePortfolioImage(i)} style={S.removeBtn} type="button"><X size={14} /></button>
                                </div>
                            ))}
                            {portfolioPreviews.length < 20 && (
                                <label style={{ ...S.uploadBox, ...S.portfolioItem }}>
                                    <Plus size={24} color="#94a3b8" />
                                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>추가</span>
                                    <input type="file" accept="image/*" multiple onChange={handlePortfolioImagesChange} style={S.fileInput} />
                                </label>
                            )}
                        </div>
                    </div>

                    {/* 기공소 내부사진 */}
                    <div style={{ ...S.formGroup, marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                        <label style={S.label}>기공소 내부사진 (최대 10장, 각 5MB)</label>
                        <p style={S.hint}>기공소 내부, 장비, 작업 환경 등을 보여주세요</p>
                        <div style={S.portfolioGrid}>
                            {studioPreviews.map((url, i) => (
                                <div key={i} style={S.portfolioItem}>
                                    <img src={url} alt={`내부 ${i + 1}`} style={S.portfolioImg} />
                                    <button onClick={() => removeStudioImage(i)} style={S.removeBtn} type="button"><X size={14} /></button>
                                </div>
                            ))}
                            {studioPreviews.length < 10 && (
                                <label style={{ ...S.uploadBox, ...S.portfolioItem }}>
                                    <Plus size={24} color="#94a3b8" />
                                    <span style={{ fontSize: '12px', color: '#94a3b8' }}>추가</span>
                                    <input type="file" accept="image/*" multiple onChange={handleStudioImagesChange} style={S.fileInput} />
                                </label>
                            )}
                        </div>
                    </div>

                    {/* 치과 프로필용 보철물 사진 */}
                    <div style={{ ...S.formGroup, marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
                        <label style={S.label}>치과 프로필용 보철물 사진 (최대 4장, 각 5MB)</label>
                        <p style={S.hint}>거래 치과 프로필에 표시될 대표 보철물 사진입니다. 가장 잘 나온 작품 4장을 선별해서 올려주세요.</p>
                        <div style={{ padding: '8px 12px', backgroundColor: '#eef2ff', borderRadius: 8, marginBottom: 12, fontSize: 12, color: '#4f46e5' }}>
                            💡 이 사진은 거래 치과의 프로필 페이지에 "기공소 보철 포트폴리오"로 노출됩니다
                        </div>
                        <div style={S.portfolioGrid}>
                            {clinicShowcasePreviews.map((url, i) => (
                                <div key={i} style={S.portfolioItem}>
                                    <img src={url} alt={`치과용 ${i + 1}`} style={S.portfolioImg} />
                                    <button onClick={() => setClinicShowcasePreviews(prev => prev.filter((_, idx) => idx !== i))} style={S.removeBtn} type="button"><X size={14} /></button>
                                </div>
                            ))}
                            {clinicShowcasePreviews.length < 4 && (
                                <label style={{ ...S.uploadBox, ...S.portfolioItem }}>
                                    <Plus size={24} color="#6366f1" />
                                    <span style={{ fontSize: '12px', color: '#6366f1' }}>추가</span>
                                    <input type="file" accept="image/*" multiple onChange={async (e) => {
                                        const files = Array.from(e.target.files);
                                        if (clinicShowcasePreviews.length + files.length > 4) { showError('치과 프로필용 사진은 최대 4장까지 업로드 가능합니다.'); return; }
                                        for (const file of files) {
                                            if (file.size > 5 * 1024 * 1024) { showError('5MB 이하 파일만 가능합니다.'); continue; }
                                            const path = `lab-showcase/${user.uid}/${Date.now()}_${file.name}`;
                                            const storageRef = ref(storage, path);
                                            await uploadBytes(storageRef, file);
                                            const url = await getDownloadURL(storageRef);
                                            setClinicShowcasePreviews(prev => [...prev, url]);
                                        }
                                    }} style={S.fileInput} />
                                </label>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ============ SECTION 5: 연락처 ============ */}
            <div style={S.card}>
                <SectionHeader cfg={SECTION_CONFIG[4]} />
                <div style={S.sectionBody}>
                    <div style={S.formRow}>
                        <div style={{ ...S.formGroup, flex: 1 }}>
                            <label style={S.label}>전화번호</label>
                            <input type="text" value={formData.contactPhone} onChange={e => updateForm('contactPhone', e.target.value)}
                                placeholder="010-0000-0000" style={S.input} />
                        </div>
                        <div style={{ ...S.formGroup, flex: 1 }}>
                            <label style={S.label}>이메일</label>
                            <input type="text" value={formData.contactEmail} onChange={e => updateForm('contactEmail', e.target.value)}
                                placeholder="email@example.com" style={S.input} />
                        </div>
                    </div>
                    <div style={S.formRow}>
                        <div style={{ ...S.formGroup, flex: 1 }}>
                            <label style={S.label}>웹사이트</label>
                            <input type="text" value={formData.contactWebsite} onChange={e => updateForm('contactWebsite', e.target.value)}
                                placeholder="www.example.com" style={S.input} />
                        </div>
                        <div style={{ ...S.formGroup, flex: 1 }}>
                            <label style={S.label}>영업시간</label>
                            <input type="text" value={formData.workingHours} onChange={e => updateForm('workingHours', e.target.value)}
                                placeholder="평일 09:00~18:00" style={S.input} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Floating Save Button */}
            <div style={S.floatingBar}>
                <button onClick={handleSave} disabled={saving || uploadingProfile || uploadingPortfolio} style={S.saveBtn}>
                    <Save size={18} />
                    {saving ? '저장 중...' : uploadingProfile || uploadingPortfolio ? '업로드 중...' : '저장'}
                </button>
                <button onClick={handleTogglePublic} disabled={saving} style={{ ...S.activateBtn, ...(isPublic ? { backgroundColor: '#64748b' } : {}) }}>
                    {isPublic ? '비공개 전환' : '광고 활성화'}
                </button>

            </div>
        </div>
    );
}

// =========================================================================
// STYLES
// =========================================================================
const S = {
    container: { maxWidth: '800px', margin: '0 auto', padding: '24px 16px 100px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
    loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '50vh', gap: '16px', color: '#64748b' },
    spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },

    pageHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
    pageTitle: { fontSize: '24px', fontWeight: '800', color: '#0f172a', margin: 0 },
    pageSubtitle: { fontSize: '14px', color: '#64748b', marginTop: '4px' },

    card: { background: '#fff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '24px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },

    sectionHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' },
    sectionNum: { width: '28px', height: '28px', borderRadius: '8px', backgroundColor: '#6366f1', color: '#fff', fontSize: '13px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    sectionIcon: { display: 'flex', alignItems: 'center' },
    sectionTitle: { fontSize: '17px', fontWeight: '700', color: '#0f172a', margin: 0 },

    sectionBody: { display: 'flex', flexDirection: 'column', gap: '16px' },
    formGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
    formRow: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
    hint: { fontSize: '12px', color: '#94a3b8', margin: 0 },

    input: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', color: '#0f172a', outline: 'none', transition: 'border-color 0.2s', backgroundColor: '#f8fafc' },
    textarea: { padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', color: '#0f172a', outline: 'none', resize: 'vertical', fontFamily: 'inherit', backgroundColor: '#f8fafc', lineHeight: '1.6' },

    inputWithSuffix: { display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: '10px', backgroundColor: '#f8fafc', overflow: 'hidden' },
    inputInner: { flex: 1, padding: '10px 14px', border: 'none', outline: 'none', fontSize: '14px', color: '#0f172a', backgroundColor: 'transparent' },
    suffix: { padding: '10px 14px', fontSize: '13px', color: '#64748b', backgroundColor: '#f1f5f9', fontWeight: '600', whiteSpace: 'nowrap' },

    pillWrap: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
    pill: { padding: '8px 16px', borderRadius: '20px', border: '1px solid #e2e8f0', backgroundColor: '#f8fafc', color: '#475569', fontSize: '13px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.15s', outline: 'none' },
    pillActive: { backgroundColor: '#6366f1', color: '#fff', borderColor: '#6366f1' },

    table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
    th: { padding: '10px 12px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' },
    td: { padding: '8px 12px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
    tableInput: { width: '100%', padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', fontSize: '13px', outline: 'none', backgroundColor: '#fff', boxSizing: 'border-box' },
    iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' },

    portfolioGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '12px' },
    portfolioItem: { width: '100%', aspectRatio: '1', position: 'relative', borderRadius: '12px', overflow: 'hidden' },
    portfolioImg: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: '12px', border: '1px solid #e2e8f0' },

    uploadBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px', border: '2px dashed #e2e8f0', borderRadius: '12px', cursor: 'pointer', backgroundColor: '#f8fafc', transition: 'border-color 0.2s' },
    fileInput: { display: 'none' },
    removeBtn: { position: 'absolute', top: '6px', right: '6px', width: '24px', height: '24px', borderRadius: '50%', backgroundColor: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },

    iconBox: { width: '40px', height: '40px', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    toggle: { width: '52px', height: '28px', borderRadius: '14px', border: 'none', cursor: 'pointer', position: 'relative', transition: 'background-color 0.2s', padding: 0 },
    toggleKnob: { width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#fff', position: 'absolute', top: '3px', left: '3px', transition: 'transform 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' },

    statusActive: { display: 'flex', alignItems: 'center', gap: '4px', padding: '4px 10px', backgroundColor: '#dcfce7', color: '#16a34a', borderRadius: '6px', fontSize: '12px', fontWeight: '600' },
    expiryBadge: { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: '#f0f9ff', borderRadius: '8px', fontSize: '13px', color: '#0369a1', fontWeight: '600' },
    sampleBtn: { padding: '8px 16px', background: '#f59e0b', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },

    successMsg: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', backgroundColor: '#dcfce7', color: '#16a34a', borderRadius: '12px', marginBottom: '16px', fontSize: '14px', fontWeight: '500' },
    errorMsg: { display: 'flex', alignItems: 'center', gap: '10px', padding: '14px 18px', backgroundColor: '#fef2f2', color: '#dc2626', borderRadius: '12px', marginBottom: '16px', fontSize: '14px', fontWeight: '500' },

    floatingBar: { position: 'fixed', bottom: 0, left: 0, right: 0, padding: '16px 24px', backgroundColor: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'center', gap: '12px', zIndex: 100 },
    saveBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 32px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(99,102,241,0.3)' },
    activateBtn: { padding: '12px 24px', backgroundColor: '#10b981', color: '#fff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },

    // 프리미엄 홍보 사전 신청
    promoCard: { background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)', borderRadius: '16px', border: '1px solid #c7d2fe', padding: '24px', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
    promoHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' },
    promoIconBox: { width: '44px', height: '44px', borderRadius: '12px', background: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    promoTitle: { fontSize: '18px', fontWeight: '800', color: '#312e81' },
    promoBadge: { display: 'inline-block', marginTop: '4px', padding: '3px 10px', borderRadius: '20px', backgroundColor: '#e0e7ff', color: '#4338ca', fontSize: '12px', fontWeight: '700' },
    promoLead: { fontSize: '14px', color: '#334155', lineHeight: '1.6', margin: '0 0 16px' },
    promoInfoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px', marginBottom: '16px' },
    promoInfoItem: { background: '#fff', borderRadius: '10px', padding: '12px 14px', border: '1px solid #e0e7ff' },
    promoInfoLabel: { fontSize: '12px', color: '#6366f1', fontWeight: '700', marginBottom: '4px' },
    promoInfoValue: { fontSize: '14px', color: '#0f172a', fontWeight: '700' },
    promoNotice: { display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px 14px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '10px', fontSize: '13px', color: '#92400e', lineHeight: '1.55', marginBottom: '16px' },
    promoProgressWrap: { marginBottom: '16px' },
    promoProgressText: { fontSize: '13px', fontWeight: '700', color: '#4338ca', marginBottom: '6px' },
    promoProgressBar: { width: '100%', height: '8px', borderRadius: '6px', backgroundColor: '#e0e7ff', overflow: 'hidden' },
    promoProgressFill: { height: '100%', borderRadius: '6px', background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', transition: 'width 0.3s' },
    promoAppliedBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', padding: '16px' },
    promoCancelBtn: { marginTop: '12px', padding: '8px 18px', backgroundColor: '#fff', color: '#64748b', border: '1px solid #cbd5e1', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
    promoFormBox: { background: '#fff', borderRadius: '12px', padding: '16px', border: '1px solid #e0e7ff' },
    promoFormRow: { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
    promoApplyBtn: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', width: '100%', padding: '13px', backgroundColor: '#6366f1', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '700', cursor: 'pointer', boxShadow: '0 4px 12px rgba(99,102,241,0.25)' },
};

export default LabAdvertising;
